import { ListModel } from "shared/interfaces/core-interfaces";
import { SelectedRootDto } from "features/TimeTracking/state/timeTrackingInterfaces";

// Response DTOs
export interface CostCenterTrackingSessionResponse {
    id: string;
    date: string;
    employeeId: string;
    employee: string;
    position: string;
    projectId: string;
    project: string;
    projectType: string;
    selections: SelectedRootDto[];
    description: string;
    time: string;
    hoursIndustrial: number;
}

export interface EmployeeTotalTimeResponse {
    employeeId: string;
    employee: string;
    tenantId: string;
    tenant: string;
    time: string;
    hoursIndustrial: number;
}

export interface ProjectTotalTimeResponse {
    projectId: string;
    project: string;
    projectType: string;
    tenantId: string;
    tenant: string;
    time: string;
    hoursIndustrial: number;
}

export interface TotalTimePerEmployeeAndProjectResponse {
    employeeId: string;
    employee: string;
    department: string;
    tenantId: string;
    tenant: string;
    totalTimePerProject: ProjectTotalTimeResponse[];
    timeSum: string;
    hoursIndustrialSum: number;
}

// One row per profit/cost center, summed across all tenants in the report.
export interface ProjectTotalTimeAllTenantsResponse {
    projectId: string;
    project: string;
    projectType: string;
    time: string;
    hoursIndustrial: number;
}

export interface TimeReportDataResponse {
    costCenterTrackingSessions: CostCenterTrackingSessionResponse[];
    totalTimePerEmployees: EmployeeTotalTimeResponse[];
    totalTimePerProject: ProjectTotalTimeResponse[];
    totalTimePerEmployeesAndProjects: TotalTimePerEmployeeAndProjectResponse[];
    totalTimePerProjectAllTenants: ProjectTotalTimeAllTenantsResponse[];
}

// Filter / request model (matches backend GetTimeReportRequest)
export interface TimeReportFilterRequest {
    tenantIds: string[];
    employeeIds: string[];
    projectIds: string[];
    from: string | null;
    to: string | null;
}

export const initTimeReportFilterRequest: TimeReportFilterRequest = {
    tenantIds: [],
    employeeIds: [],
    projectIds: [],
    from: null,
    to: null,
};

// Export column picker (matches backend TimeReportColumns / TimeReportExportColumns).
// Flags left out default to visible on the backend, so each sheet only sends the ones it has.
export interface TimeReportColumnFlags {
    date?: boolean;
    employee?: boolean;
    position?: boolean;
    project?: boolean;
    projectType?: boolean;
    phase?: boolean;
    description?: boolean;
    hours?: boolean;
    industrialHours?: boolean;
    company?: boolean;
    department?: boolean;
    partner?: boolean;
}

// One entry per exported sheet, mirroring the five tabs of the screen.
export interface TimeReportExportColumns {
    sessions: TimeReportColumnFlags;
    byEmployee: TimeReportColumnFlags;
    byProject: TimeReportColumnFlags;
    byEmployeeAndProject: TimeReportColumnFlags;
    byProjectAllTenants: TimeReportColumnFlags;
}

// Row filtering per exported sheet — the grid's column filters and global search, replayed
// server-side so each sheet holds the same rows its tab is showing.
export interface TimeReportColumnFilter {
    id: string;
    value: string;
}

export interface TimeReportSheetFilter {
    columnFilters: TimeReportColumnFilter[];
    globalFilter: string;
}

export interface TimeReportExportFilters {
    sessions: TimeReportSheetFilter;
    byEmployee: TimeReportSheetFilter;
    byProject: TimeReportSheetFilter;
    byEmployeeAndProject: TimeReportSheetFilter;
    byProjectAllTenants: TimeReportSheetFilter;
}

export interface TimeReportExportRequest extends TimeReportFilterRequest {
    // Collapses sessions that agree on date, employee, center, phases and description into one
    // summed row. Only the Sessions sheet changes; the aggregate sheets are unaffected.
    mergeTrackedEntries: boolean;
    columns: TimeReportExportColumns;
    filters: TimeReportExportFilters;
}

// Create data response (dropdown options)
export interface TimeReportCreateDataResponse {
    tenants: ListModel<string>[];
    // keyed by tenant id, so the employee dropdown can follow the tenant selection
    employees: Record<string, ListModel<string>[]>;
    projects: ListModel<string>[];
}

// Slice state
export interface TimeReportState {
    timeReportData: TimeReportDataResponse | null;
    createData: TimeReportCreateDataResponse | null;
    activeFilter: TimeReportFilterRequest | null;
}

export const initialTimeReportState: TimeReportState = {
    timeReportData: null,
    createData: null,
    activeFilter: null,
};
